import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Trophy,
  Package,
  BarChart3,
  Settings,
  Shield,
  Menu,
  X,
  LogOut,
  ChevronRight,
  Activity,
  FileText,
  Lightbulb,
  Sparkles,
} from 'lucide-react';
import useUserStore from '../store/userStore';
import useAdminStore from '../store/adminStore';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import ChallengeEditor from './pages/ChallengeEditor';
import ComponentEditor from './pages/ComponentEditor';
import SolutionEditor from './pages/SolutionEditor';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import SystemSettings from './pages/SystemSettings';
import PolicyEditor from './pages/PolicyEditor';
import LeadMagnetManager from './pages/LeadMagnetManager';

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/challenges', icon: Trophy, label: 'Challenges' },
  { path: '/admin/solutions', icon: Lightbulb, label: 'Solutions' },
  { path: '/admin/components', icon: Package, label: 'Components' },
  { path: '/admin/lead-magnet', icon: Sparkles, label: 'Lead Magnet' },
  { path: '/admin/policies', icon: FileText, label: 'Policies' },
  { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
];

const AdminSidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useUserStore();

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-gray-900 text-white z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <Link to="/admin" className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-blue-400" />
              <span className="text-xl font-bold">Admin Panel</span>
            </Link>
            <button onClick={onClose} className="lg:hidden text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path, item.exact)
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {isActive(item.path, item.exact) && (
                  <ChevronRight className="w-4 h-4 ml-auto" />
                )}
              </Link>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center font-bold">
                {user?.username?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.username}</div>
                <div className="text-xs text-gray-400 truncate">{user?.email}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Link
                to="/"
                className="flex-1 px-3 py-2 text-sm text-center text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Back to App
              </Link>
              <button
                onClick={logout}
                className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

const AdminHeader = ({ onMenuClick }) => {
  const location = useLocation();

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin') return 'Dashboard';
    if (path.includes('/users')) return 'User Management';
    if (path.includes('/challenges')) return 'Challenge Editor';
    if (path.includes('/solutions')) return 'Solution Editor';
    if (path.includes('/components')) return 'Component Editor';
    if (path.includes('/lead-magnet')) return 'Lead Magnet Manager';
    if (path.includes('/policies')) return 'Policy Pages';
    if (path.includes('/analytics')) return 'Analytics';
    if (path.includes('/settings')) return 'System Settings';
    return 'Admin';
  };

  return (
    <header className="bg-white border-b shadow-sm sticky top-0 z-30">
      <div className="flex items-center justify-between px-4 h-16">
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg lg:hidden"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-semibold text-gray-800">{getPageTitle()}</h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Activity className="w-4 h-4" />
            <span>Admin Mode</span>
          </div>
        </div>
      </div>
    </header>
  );
};

const AdminApp = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated } = useUserStore();
  const { isAdmin, loading, checkAdminAccess } = useAdminStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      checkAdminAccess();
    }
  }, [isAuthenticated]);

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Loading state
  if (loading && isAdmin === null) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Checking admin access...</p>
        </div>
      </div>
    );
  }

  // Access denied
  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You don't have admin privileges. Please contact an administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="lg:ml-64">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} />

        <main className="p-6">
          <Routes>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="challenges" element={<ChallengeEditor />} />
            <Route path="solutions" element={<SolutionEditor />} />
            <Route path="components" element={<ComponentEditor />} />
            <Route path="lead-magnet" element={<LeadMagnetManager />} />
            <Route path="policies" element={<PolicyEditor />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="settings" element={<SystemSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminApp;
